import React, {useState, useEffect} from 'react';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import {BulkEmailDTO} from '../../services/apiClient';
import useCommunicationLists from '../../hooks/useCommunicationLists';
import FileUpload from "../common/FileUpload";
import handleChange from '../../utils/formUtils/handleChange';

interface BulkEmailFormProps {
    initialValues: BulkEmailDTO;
    setInitialValues: (values: BulkEmailDTO) => void;
    setAttachmentFile: (file: File | null) => void;
    onSubmit: (data: BulkEmailDTO) => void;
    onCancel: () => void;
}

const BulkEmailForm: React.FC<BulkEmailFormProps> = ({
                                                         initialValues,
                                                         setInitialValues,
                                                         setAttachmentFile,
                                                         onSubmit,
                                                         onCancel,
                                                     }) => {
    const {communicationLists, isLoading, error} = useCommunicationLists();
    const [replacementValues, setReplacementValues] = useState<Record<string, string>>({});
    const [formData, setFormData] = useState(initialValues);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setAttachmentFile(e.target.files[0]);
        }
    };

    const handleDeleteAttachment = () => {
        setInitialValues({...formData, attachmentPath: ''});
        setAttachmentFile(null); // Clear the file input
    };

    const updateReplacementValues = (communicationListId: string | undefined) => {
        const selectedCommunicationList = communicationLists.find(
            (list) => list.id === communicationListId
        );

        if (selectedCommunicationList?.metadata) {
            try {
                const metadata = JSON.parse(selectedCommunicationList.metadata);
                const values = metadata?.ReplacementValues || {};
                setReplacementValues(values);
            } catch (error) {
                console.error('Failed to parse metadata:', error);
                setReplacementValues({});
            }
        } else {
            setReplacementValues({});
        }
    };

    useEffect(() => {
        updateReplacementValues(formData.communicationListId);
    }, [formData.communicationListId, communicationLists]);

    useEffect(() => {
        if (!formData.communicationListId && communicationLists.length > 0) {
            const defaultCommunicationListId = communicationLists[0]?.id;
            setInitialValues({...formData, communicationListId: defaultCommunicationListId});
            updateReplacementValues(defaultCommunicationListId);
        }
    }, [communicationLists]);

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <form onSubmit={handleFormSubmit}>
            <div className="mb-4">
                {isLoading ? (
                    <p>Loading communication lists...</p>
                ) : error ? (
                    <p>Error loading communication lists: {error}</p>
                ) : (
                    <FormField
                        id="communicationListId"
                        label="Communication List"
                        type="dropdown"
                        value={formData.communicationListId || ''}
                        onChange={(e) => handleChange(e, setFormData)}
                        options={communicationLists.map((list) => ({
                            value: list.id,
                            label: list.name,
                        }))}
                        placeholder="Select a communication list"
                    />
                )}
            </div>
            {replacementValues && Object.keys(replacementValues).length > 0 && (
                <div className="mb-4 p-4 bg-gray-100 dark:bg-slate-800 rounded shadow">
                    <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">
                        Available Replacement Values:
                    </h3>
                    <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-400">
                        {Object.entries(replacementValues).map(([placeholder]) => (
                            <li key={placeholder}>
                                <strong className="text-gray-800 dark:text-gray-200">{placeholder}</strong>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <div className="mb-4">
                <FormField
                    id="subject"
                    label="Subject"
                    type="text"
                    value={formData.subject || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter email subject"
                />
            </div>
            <div className="mb-4">
                <FormField
                    id="title"
                    label="Title"
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter title"
                />
            </div>
            <div className="mb-4">
                <FormField
                    id="content"
                    label="Content"
                    type="richtext"
                    value={formData.content || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter email content"
                />
            </div>
            <div className="mb-4">
                <label htmlFor="attachmentPath" className="block text-sm font-medium text-gray-700">
                    Attachment
                </label>
                {formData.attachmentPath ? (
                    <div className="flex items-center space-x-4 mt-2">
                        <span className="text-sm text-gray-600">{formData.attachmentPath}</span>
                        <Button
                            variant="danger"
                            onClick={handleDeleteAttachment}
                            className="text-sm"
                        >
                            Remove
                        </Button>
                    </div>
                ) : (
                    <FileUpload
                        id="attachmentPath"
                        placeholderText="Upload or drag and drop the attachment."
                        supportedFormats = 'PDF, DOCX, XLSX, PNG, JPG up to 10MB'
                        onChange={handleFileChange}
                    />
                )}
            </div>
            <div className="flex justify-end space-x-4">
                <Button variant="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit">Save</Button>
            </div>
        </form>
    );
};

export default BulkEmailForm;
